import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import '../components/AdminLayout.css';
import './AdminSpotWorkers.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DEFAULT_DURATION_KEY = 'spot_worker.default_protection_duration_min';
const FALLBACK_DURATION = '120';
const PAGE_SIZE = 15;

// ── Types (mirror src/types/admin.ts) ──────────────────────────

interface WorkerRow {
  id: number;
  instanceId: string;
  instanceType: string | null;
  availabilityZone: string | null;
  region: string | null;
  status: string;
  liveState: string | null;
  trigger: string | null;
  launchedAt: string;
  drainStartedAt: string | null;
  terminatedAt: string | null;
  protectedUntil: string | null;
  lastHeartbeatAt: string | null;
  heartbeatStale: boolean;
  jobsCompletedCount: number;
  currentJobId: string | null;
  terminationReason: string | null;
  terminationSource: string | null;
  vcpus: number | null;
  memoryMb: number | null;
  diskTotalGb: number | null;
  spotPrice: number | null;
  billedSeconds: number | null;
  costUsd: number | null;
}

interface NeedsAttention {
  kind: 'unpicked' | 'stuck';
  jobId: string;
  userId: string;
  userName: string | null;
  projectName: string | null;
  detail: string;
}

interface LaunchLog {
  id: number;
  instanceId: string;
  trigger: string | null;
  launchedAt: string;
  terminatedAt: string | null;
  jobsCompletedCount: number;
  billedSeconds: number | null;
  costUsd: number | null;
  terminationSource: string | null;
  status: string;
}

interface Fleet {
  running: number;
  draining: number;
  inFlightJobs: number;
  queuedJobs: number;
  oldestQueuedWaitSec: number | null;
  spend24hUsd: number;
  interruptions24h: number;
  liveStateAvailable: boolean;
  activeWorkers: WorkerRow[];
  needsAttention: NeedsAttention[];
  recentLaunches: LaunchLog[];
}

interface JobAttempt {
  id: string;
  jobId: string;
  jobKind: string;
  userId: string | null;
  userName: string | null;
  projectName: string | null;
  spotWorkerLaunchId: number | null;
  instanceId: string | null;
  attemptNumber: number;
  pickedAt: string | null;
  completedAt: string | null;
  releasedAt: string | null;
  durationSec: number | null;
  outcome: string | null;
  error: string | null;
  workerMetrics: Record<string, unknown>;
}

interface WorkerDetail extends WorkerRow {
  attempts: JobAttempt[];
}

interface QueuedJob {
  jobId: string;
  jobKind: string;
  userId: string;
  userName: string | null;
  projectName: string | null;
  status: string;
  createdAt: string;
  waitSec: number;
}

interface Cost {
  rangeDays: number;
  totalUsd: number;
  perJobUsd: number | null;
  wastedUsd: number;
  byTrigger: Array<{ trigger: string; launches: number; costUsd: number }>;
  interruptions: Array<{ source: string; count: number }>;
  launchLog: LaunchLog[];
}

type Tab = 'overview' | 'workers' | 'jobs' | 'settings';
type JobBucket = 'queued' | 'inflight' | 'recent' | 'stuck';

// ── Formatters ─────────────────────────────────────────────────

function fmtDur(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${sec % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
function fmtAge(iso: string | null): string {
  if (!iso) return '—';
  return fmtDur(Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000)));
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtMoney(n: number | null): string {
  return n == null ? '—' : `$${n.toFixed(n < 1 ? 3 : 2)}`;
}
function metric(m: Record<string, unknown>, key: string): string {
  const v = m?.[key];
  return v == null ? '—' : String(v);
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/api/admin${path}`, { credentials: 'include' });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* no body */ }
    throw new Error(detail ? `${detail} (${res.status})` : `Request failed (${res.status})`);
  }
  return res.json();
}

// User cell — links to the Users section.
const UserLink: React.FC<{ userId: string | null; userName: string | null }> = ({ userId, userName }) => {
  if (!userId) return <span className="admin-cell-muted">—</span>;
  return (
    <Link to={`/admin/users/${userId}`} className="admin-asset-link">
      {userName || `${userId.slice(0, 8)}…`}
    </Link>
  );
};

// Numbered pager (Prev · 1 2 … N · Next). Pages are 0-indexed; labels are 1-indexed.
const Pager: React.FC<{ page: number; pageCount: number; onPage: (p: number) => void }> = ({ page, pageCount, onPage }) => {
  if (pageCount <= 1) return null;
  const nums: Array<number | '…'> = [];
  for (let i = 0; i < pageCount; i++) {
    if (i === 0 || i === pageCount - 1 || Math.abs(i - page) <= 1) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  return (
    <div className="sw-pager">
      <button className="sw-pager-num" disabled={page === 0} onClick={() => onPage(page - 1)}>Prev</button>
      {nums.map((n, i) => n === '…'
        ? <span key={`gap-${i}`} className="sw-pager-gap">…</span>
        : <button key={n} className={`sw-pager-num ${n === page ? 'active' : ''}`} onClick={() => onPage(n)}>{n + 1}</button>)}
      <button className="sw-pager-num" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────

const AdminSpotWorkers: React.FC = () => {
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Launch controls
  const [defaultDuration, setDefaultDuration] = useState(FALLBACK_DURATION);
  const [launchDuration, setLaunchDuration] = useState(FALLBACK_DURATION);
  const [savingDefault, setSavingDefault] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Per-tab data + load bookkeeping
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [workers, setWorkers] = useState<WorkerRow[] | null>(null);
  const [workersTotal, setWorkersTotal] = useState(0);
  const [jobs, setJobs] = useState<{ attempts: JobAttempt[]; queued: QueuedJob[] } | null>(null);
  const [cost, setCost] = useState<Cost | null>(null);
  const [loadedAt, setLoadedAt] = useState<Partial<Record<Tab, number>>>({});
  const [busy, setBusy] = useState(false);

  // Filters + pagination
  const [wStatus, setWStatus] = useState('');
  const [wTrigger, setWTrigger] = useState('');
  const [wSince, setWSince] = useState('24');
  const [wPage, setWPage] = useState(0);
  const [jobBucket, setJobBucket] = useState<JobBucket>('recent');
  const [jobsPage, setJobsPage] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [detail, setDetail] = useState<Record<number, WorkerDetail>>({});

  const fetchTab = useCallback(async (t: Tab, opts?: { page?: number }) => {
    if (t === 'settings') return; // static form, nothing to fetch
    setBusy(true);
    setError(null);
    try {
      if (t === 'overview') {
        setFleet(await apiGet<Fleet>('/spot-workers/fleet'));
      } else if (t === 'workers') {
        const page = opts?.page ?? wPage;
        const p = new URLSearchParams();
        if (wStatus) p.set('status', wStatus);
        if (wTrigger) p.set('trigger', wTrigger);
        if (wSince) p.set('sinceHours', wSince);
        p.set('limit', String(PAGE_SIZE));
        p.set('offset', String(page * PAGE_SIZE));
        // The spend blocks up top cover the same window as the "since" filter.
        const rangeDays = Math.max(1, Math.round(Number(wSince || '168') / 24));
        const [data, costData] = await Promise.all([
          apiGet<{ workers: WorkerRow[]; total: number }>(`/spot-workers/history?${p}`),
          // Best-effort — the spend blocks disappear if this fails, the table still loads.
          apiGet<Cost>(`/spot-workers/cost?rangeDays=${rangeDays}`).catch(() => null),
        ]);
        setWorkers(data.workers);
        setWorkersTotal(data.total);
        setCost(costData);
        setExpanded(null);
      } else if (t === 'jobs') {
        setJobs(await apiGet(`/spot-workers/jobs?bucket=${jobBucket}`));
      }
      setLoadedAt((prev) => ({ ...prev, [t]: Date.now() }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
      setLoadedAt((prev) => ({ ...prev, [t]: Date.now() })); // stop the first-open effect from retrying
    } finally {
      setBusy(false);
    }
  }, [wStatus, wTrigger, wSince, wPage, jobBucket]);

  // Load default protection duration once.
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/defaults`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        const row = (d.defaults || []).find((x: { key: string }) => x.key === DEFAULT_DURATION_KEY);
        const value = row?.value || FALLBACK_DURATION;
        setDefaultDuration(value);
        setLaunchDuration(value);
      })
      .catch(() => undefined);
  }, []);

  // Load a tab the first time it is opened.
  useEffect(() => {
    if (!loadedAt[tab]) fetchTab(tab);
  }, [tab, loadedAt, fetchTab]);

  const handleSaveDefault = async () => {
    setSavingDefault(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/defaults/single`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: DEFAULT_DURATION_KEY, value: defaultDuration }),
      });
      if (!res.ok) throw new Error();
      setSuccess('Default duration saved.');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to save default duration');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/spot-workers/launch`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ durationMinutes: Number(launchDuration) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSuccess(data.launchTriggered ? 'Spot instance launch triggered.' : 'Already at MAX_WORKERS cap — no new instance launched.');
      // Fleet counts are now stale — drop the cache so Overview refetches on next visit.
      setLoadedAt((prev) => { const n = { ...prev }; delete n.overview; return n; });
      if (tab === 'overview') fetchTab('overview');
    } catch {
      setError('Failed to trigger spot instance launch');
    } finally {
      setLaunching(false);
    }
  };

  const toggleExpand = async (id: number) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        const d = await apiGet<WorkerDetail>(`/spot-workers/${id}`);
        setDetail((prev) => ({ ...prev, [id]: d }));
      } catch {
        setError('Failed to load worker detail');
      }
    }
  };

  const updated = loadedAt[tab];

  return (
    <AdminLayout
      topbarTitle="Spot Workers"
      topbarSubtitle="Fleet, jobs, and cost for the media-pipeline spot workers."
    >
      {error && <div className="admin-error">{error}</div>}
      {success && <div className="admin-success">{success}</div>}

      {/* Tabs + manual refresh */}
      <div className="sw-toolbar">
        <div className="admin-tab-bar admin-tab-bar-spaced">
          {(['overview', 'workers', 'jobs', 'settings'] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`admin-tab-btn ${tab === t ? 'active' : ''}`}>
              {t === 'settings' ? 'Launch Settings' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        {tab !== 'settings' && (
          <div className="sw-refresh">
            {updated && <span className="sw-updated">updated {new Date(updated).toLocaleTimeString()}</span>}
            <button className="admin-btn admin-btn-secondary" disabled={busy} onClick={() => fetchTab(tab)}>
              {busy ? 'Refreshing…' : '⟳ Refresh'}
            </button>
          </div>
        )}
      </div>

      {tab === 'settings' ? (
        <div className="admin-card">
          <h3 className="admin-section-title">Start Spot Instance</h3>
          <p className="admin-field-hint">
            Launches a new spot worker (respects MAX_WORKERS) and protects it from the reaper's idle-termination until the duration below elapses.
          </p>
          <div className="admin-stack admin-stack-gap-20">
            <div>
              <label className="admin-field-label admin-field-label-strong">Protection duration (minutes)</label>
              <input className="admin-input admin-input-md" type="number" min="1"
                value={launchDuration} onChange={(e) => setLaunchDuration(e.target.value)} />
            </div>
            <button className="admin-btn admin-btn-primary" disabled={launching} onClick={handleLaunch}>
              {launching ? 'Launching…' : 'Start Spot Instance'}
            </button>
          </div>
          <hr className="admin-divider" />
          <h3 className="admin-section-title">Default Protection Duration</h3>
          <p className="admin-field-hint">Pre-fills the launch form above (minutes).</p>
          <div className="admin-stack admin-stack-gap-20">
            <input className="admin-input admin-input-md" type="number" min="1"
              value={defaultDuration} onChange={(e) => setDefaultDuration(e.target.value)} />
            <button className="admin-btn admin-btn-primary" disabled={savingDefault} onClick={handleSaveDefault}>
              {savingDefault ? 'Saving…' : 'Save Default'}
            </button>
          </div>
        </div>
      ) : busy && !updated ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          {tab === 'overview' && (fleet
            ? <OverviewTab fleet={fleet} />
            : <div className="admin-card"><p className="admin-field-hint">No fleet data. If the error above mentions a missing column or relation, the spot-worker DB migration has not run yet.</p></div>)}
          {tab === 'workers' && (
            <WorkersTab
              rows={workers || []} cost={cost}
              page={wPage} pageCount={Math.max(1, Math.ceil(workersTotal / PAGE_SIZE))}
              onPage={(p) => { setWPage(p); fetchTab('workers', { page: p }); }}
              wStatus={wStatus} wTrigger={wTrigger} wSince={wSince}
              setWStatus={setWStatus} setWTrigger={setWTrigger} setWSince={setWSince}
              onApply={() => { setWPage(0); fetchTab('workers', { page: 0 }); }}
              expanded={expanded} detail={detail} onToggle={toggleExpand}
            />
          )}
          {tab === 'jobs' && (
            <JobsTab
              data={jobs} bucket={jobBucket}
              page={jobsPage} onPage={setJobsPage}
              setBucket={(b) => { setJobBucket(b); setJobsPage(0); }}
              onApply={() => fetchTab('jobs')}
            />
          )}
        </>
      )}
    </AdminLayout>
  );
};

// ── Overview ───────────────────────────────────────────────────

const OverviewTab: React.FC<{ fleet: Fleet }> = ({ fleet }) => (
  <>
    <div className="admin-card">
      <div className="admin-stats-grid">
        {[
          { label: 'Running', value: `${fleet.running}${fleet.draining ? ` (+${fleet.draining} draining)` : ''}` },
          { label: 'Jobs in-flight', value: fleet.inFlightJobs },
          { label: 'Queued (unpicked)', value: fleet.queuedJobs },
          { label: 'Oldest wait', value: fmtDur(fleet.oldestQueuedWaitSec) },
          { label: 'Spend 24h', value: fmtMoney(fleet.spend24hUsd) },
          { label: 'Interruptions 24h', value: fleet.interruptions24h },
        ].map((s) => (
          <div key={s.label} className="admin-stat-card">
            <div className="admin-stat-value">{s.value}</div>
            <div className="admin-stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>

    {!fleet.liveStateAvailable && (
      <div className="sw-banner">⚠ pipeline-manager unreachable — showing DB status only.</div>
    )}

    <div className="admin-card">
      <h3 className="admin-section-title">Active workers</h3>
      {fleet.activeWorkers.length === 0 ? (
        <p className="admin-field-hint">No active spot workers.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Instance</th><th>Type</th><th>AZ</th><th>State</th><th>Age</th>
              <th>Heartbeat</th><th>Jobs</th><th>Current job</th>
            </tr>
          </thead>
          <tbody>
            {fleet.activeWorkers.map((w) => (
              <tr key={w.id}>
                <td className="sw-mono">{w.instanceId}</td>
                <td>{w.instanceType || '—'}</td>
                <td>{w.availabilityZone || '—'}</td>
                <td>{w.liveState || w.status}</td>
                <td>{fmtAge(w.launchedAt)}</td>
                <td>
                  {w.heartbeatStale
                    ? <span className="admin-badge sw-badge-stale">stale</span>
                    : <span className="admin-cell-muted">{fmtAge(w.lastHeartbeatAt)}</span>}
                </td>
                <td>{w.jobsCompletedCount}</td>
                <td className="sw-mono">{w.currentJobId ? w.currentJobId.slice(0, 8) + '…' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>

    <RecentLaunches launches={fleet.recentLaunches} />

    {fleet.needsAttention.length > 0 && (
      <div className="admin-card">
        <h3 className="admin-section-title">Needs attention</h3>
        <ul className="sw-attention-list">
          {fleet.needsAttention.map((a) => (
            <li key={`${a.kind}-${a.jobId}`}>
              <span className={`admin-badge ${a.kind === 'stuck' ? 'sw-badge-failed' : 'sw-badge-interrupted'}`}>{a.kind}</span>
              <span className="sw-mono">{a.jobId.slice(0, 8)}…</span>
              <span>{a.projectName || '—'}</span>
              <UserLink userId={a.userId} userName={a.userName} />
              <span>{a.detail}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </>
);

// ── Workers ────────────────────────────────────────────────────

const WorkersTab: React.FC<{
  rows: WorkerRow[];
  cost: Cost | null;
  page: number; pageCount: number; onPage: (p: number) => void;
  wStatus: string; wTrigger: string; wSince: string;
  setWStatus: (v: string) => void; setWTrigger: (v: string) => void; setWSince: (v: string) => void;
  onApply: () => void;
  expanded: number | null;
  detail: Record<number, WorkerDetail>;
  onToggle: (id: number) => void;
}> = ({ rows, cost, page, pageCount, onPage, wStatus, wTrigger, wSince, setWStatus, setWTrigger, setWSince, onApply, expanded, detail, onToggle }) => (
  <div className="admin-card">
    <SpendBlocks cost={cost} />
    <div className="admin-filters-row">
      <select className="admin-input admin-input-sm" value={wStatus} onChange={(e) => setWStatus(e.target.value)}>
        <option value="">any status</option>
        {['pending', 'running', 'draining', 'terminated'].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select className="admin-input admin-input-sm" value={wTrigger} onChange={(e) => setWTrigger(e.target.value)}>
        <option value="">any trigger</option>
        {['enqueue', 'admin', 'launcher_cron'].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select className="admin-input admin-input-sm" value={wSince} onChange={(e) => setWSince(e.target.value)}>
        {[['24', 'last 24h'], ['72', 'last 3d'], ['168', 'last 7d'], ['720', 'last 30d']].map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
      <button className="admin-btn admin-btn-secondary" onClick={onApply}>Apply</button>
    </div>

    <table className="admin-table">
      <thead>
        <tr>
          <th>Status</th><th>Instance</th><th>Type</th><th>AZ</th><th>Trigger</th>
          <th>Launched</th><th>Ended</th><th>Term. source</th><th>Jobs</th><th>Billed</th><th>Cost</th><th></th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={12} className="admin-cell-empty">No workers.</td></tr>}
        {rows.map((w) => (
          <React.Fragment key={w.id}>
            <tr>
              <td>{w.liveState || w.status}</td>
              <td className="sw-mono">{w.instanceId}</td>
              <td>{w.instanceType || '—'}</td>
              <td>{w.availabilityZone || '—'}</td>
              <td>{w.trigger || '—'}</td>
              <td className="admin-cell-muted">{fmtDateTime(w.launchedAt)}</td>
              <td className="admin-cell-muted">{fmtDateTime(w.terminatedAt)}</td>
              <td>{w.terminationSource || '—'}</td>
              <td>{w.jobsCompletedCount}</td>
              <td>{fmtDur(w.billedSeconds)}</td>
              <td>{fmtMoney(w.costUsd)}</td>
              <td><button className="sw-expand-btn" onClick={() => onToggle(w.id)}>{expanded === w.id ? 'hide' : 'detail'}</button></td>
            </tr>
            {expanded === w.id && (
              <tr>
                <td colSpan={12} className="sw-detail-cell">
                  {!detail[w.id] ? <span className="admin-cell-muted">Loading…</span> : (
                    <>
                      <div className="sw-detail-grid">
                        <div><span>Lifecycle </span>{fmtDateTime(w.launchedAt)} → {w.drainStartedAt ? `drain ${fmtDateTime(w.drainStartedAt)} → ` : ''}{fmtDateTime(w.terminatedAt) || 'running'}</div>
                        <div><span>Reason </span>{w.terminationReason || '—'}</div>
                        <div><span>Capacity </span>{w.vcpus ?? '?'} vCPU · {w.memoryMb ?? '?'} MB · {w.diskTotalGb ?? '?'} GB</div>
                        <div><span>Spot price </span>{w.spotPrice != null ? `$${w.spotPrice}/hr` : '—'}</div>
                        <div><span>Billed </span>{fmtDur(w.billedSeconds)} → {fmtMoney(w.costUsd)}</div>
                        <div><span>Region </span>{w.region || '—'}</div>
                      </div>
                      <table className="admin-table">
                        <thead>
                          <tr><th>Job</th><th>Kind</th><th>#</th><th>Picked</th><th>Dur</th><th>Outcome</th><th>Peak RSS</th><th>Disk free (end)</th></tr>
                        </thead>
                        <tbody>
                          {detail[w.id].attempts.length === 0 && <tr><td colSpan={8} className="admin-cell-empty">No attempts.</td></tr>}
                          {detail[w.id].attempts.map((a) => (
                            <tr key={a.id}>
                              <td className="sw-mono">{a.jobId.slice(0, 8)}…</td>
                              <td>{a.jobKind}</td>
                              <td>{a.attemptNumber}</td>
                              <td className="admin-cell-muted">{fmtDateTime(a.pickedAt)}</td>
                              <td>{fmtDur(a.durationSec)}</td>
                              <td><OutcomeBadge outcome={a.outcome} /></td>
                              <td>{metric(a.workerMetrics, 'peak_rss_mb')}</td>
                              <td>{metric(a.workerMetrics, 'disk_free_mb_at_finish')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
    <Pager page={page} pageCount={pageCount} onPage={onPage} />
  </div>
);

const OutcomeBadge: React.FC<{ outcome: string | null }> = ({ outcome }) => {
  if (!outcome) return <span className="admin-cell-muted">—</span>;
  return <span className={`admin-badge sw-badge-${outcome}`}>{outcome}</span>;
};

// ── Jobs ───────────────────────────────────────────────────────

const JobsTab: React.FC<{
  data: { attempts: JobAttempt[]; queued: QueuedJob[] } | null;
  bucket: JobBucket;
  page: number;
  onPage: (p: number) => void;
  setBucket: (b: JobBucket) => void;
  onApply: () => void;
}> = ({ data, bucket, page, onPage, setBucket, onApply }) => {
  const all = bucket === 'queued' ? (data?.queued ?? []) : (data?.attempts ?? []);
  const pageCount = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const queued = bucket === 'queued' ? (all as QueuedJob[]).slice(start, start + PAGE_SIZE) : [];
  const attempts = bucket === 'queued' ? [] : (all as JobAttempt[]).slice(start, start + PAGE_SIZE);

  return (
    <div className="admin-card">
      <div className="sw-subtabs">
        {(['recent', 'inflight', 'queued', 'stuck'] as JobBucket[]).map((b) => (
          <button key={b}
            className={`admin-tab-btn ${bucket === b ? 'active' : ''}`}
            onClick={() => { setBucket(b); setTimeout(onApply, 0); }}>
            {b === 'inflight' ? 'In-flight' : b === 'recent' ? 'Recent attempts' : b.charAt(0).toUpperCase() + b.slice(1)}
          </button>
        ))}
      </div>

      {bucket === 'queued' ? (
        <table className="admin-table">
          <thead><tr><th>Job</th><th>Kind</th><th>Project</th><th>User</th><th>Enqueued</th><th>Wait</th></tr></thead>
          <tbody>
            {queued.length === 0 && <tr><td colSpan={6} className="admin-cell-empty">Nothing queued.</td></tr>}
            {queued.map((j) => (
              <tr key={j.jobId}>
                <td className="sw-mono">{j.jobId.slice(0, 8)}…</td>
                <td>{j.jobKind}</td>
                <td>{j.projectName || '—'}</td>
                <td><UserLink userId={j.userId} userName={j.userName} /></td>
                <td className="admin-cell-muted">{fmtDateTime(j.createdAt)}</td>
                <td>{fmtDur(j.waitSec)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Job</th><th>#</th><th>Worker</th><th>Kind</th><th>Project</th><th>User</th><th>Picked</th><th>Dur</th><th>Outcome</th><th>Peak RSS</th></tr>
          </thead>
          <tbody>
            {attempts.length === 0 && <tr><td colSpan={10} className="admin-cell-empty">No attempts.</td></tr>}
            {attempts.map((a) => (
              <tr key={a.id}>
                <td className="sw-mono">{a.jobId.slice(0, 8)}…</td>
                <td>{a.attemptNumber}</td>
                <td className="sw-mono">{a.instanceId || '—'}</td>
                <td>{a.jobKind}</td>
                <td>{a.projectName || '—'}</td>
                <td><UserLink userId={a.userId} userName={a.userName} /></td>
                <td className="admin-cell-muted">{fmtDateTime(a.pickedAt)}</td>
                <td>{fmtDur(a.durationSec)}</td>
                <td><OutcomeBadge outcome={a.outcome} /></td>
                <td>{metric(a.workerMetrics, 'peak_rss_mb')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pager page={page} pageCount={pageCount} onPage={onPage} />
    </div>
  );
};

// ── Spend / cost summary (merged into the Workers tab) ──────────

const SpendBlocks: React.FC<{ cost: Cost | null }> = ({ cost }) => {
  if (!cost) return null;
  return (
    <div className="sw-blocks">
      <div className="sw-block">
        <h4>Spend</h4>
        <div className="sw-block-row"><span>total</span><b>{fmtMoney(cost.totalUsd)}</b></div>
        <div className="sw-block-row"><span>per job</span><b>{fmtMoney(cost.perJobUsd)}</b></div>
        <div className="sw-block-row"><span>wasted (0-job)</span><b>{fmtMoney(cost.wastedUsd)}</b></div>
      </div>
      <div className="sw-block">
        <h4>By trigger</h4>
        {cost.byTrigger.length === 0 && <div className="admin-cell-muted">—</div>}
        {cost.byTrigger.map((t) => (
          <div key={t.trigger} className="sw-block-row"><span>{t.trigger}</span><b>{t.launches} · {fmtMoney(t.costUsd)}</b></div>
        ))}
      </div>
      <div className="sw-block">
        <h4>Interruptions</h4>
        {cost.interruptions.length === 0 && <div className="admin-cell-muted">—</div>}
        {cost.interruptions.map((it) => (
          <div key={it.source} className="sw-block-row"><span>{it.source}</span><b>{it.count}</b></div>
        ))}
      </div>
    </div>
  );
};

const RECENT_LAUNCH_PAGE_SIZE = 5;

const RecentLaunches: React.FC<{ launches: LaunchLog[] }> = ({ launches }) => {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(launches.length / RECENT_LAUNCH_PAGE_SIZE));
  const rows = launches.slice(page * RECENT_LAUNCH_PAGE_SIZE, page * RECENT_LAUNCH_PAGE_SIZE + RECENT_LAUNCH_PAGE_SIZE);
  return (
    <div className="admin-card">
      <h3 className="admin-section-title">Recent launches</h3>
      {launches.length === 0 ? (
        <p className="admin-field-hint">No launches recorded.</p>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr><th>Launched</th><th>Trigger</th><th>Instance</th><th>Jobs</th><th>Billed</th><th>Cost</th><th>End reason</th></tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id}>
                  <td className="admin-cell-muted">{fmtDateTime(l.launchedAt)}</td>
                  <td>{l.trigger || '—'}</td>
                  <td className="sw-mono">{l.instanceId}</td>
                  <td>{l.jobsCompletedCount}</td>
                  <td>{fmtDur(l.billedSeconds)}</td>
                  <td>{fmtMoney(l.costUsd)}</td>
                  <td>{l.terminationSource || (l.status === 'terminated' ? '—' : `(${l.status})`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageCount={pageCount} onPage={setPage} />
        </>
      )}
    </div>
  );
};

export default AdminSpotWorkers;
